import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

/**
 * Premium Scratch Card — dark graphite foil with gold shimmer.
 * Canvas destination-out for the scratching; prize frame revealed beneath.
 *
 * Props:
 *  - prizeLabel: string — prize text (server-picked outcome)
 *  - isWin: boolean — styles the prize frame (warm gold vs cool stone)
 *  - spinning: boolean — API call in flight
 *  - resultIndex: number|null — arms the canvas when result arrives
 *  - onStart: () => void
 *  - onRevealed: () => void
 *  - themeColor: string
 *  - disabled: boolean
 */
export default function ScratchCard({
  prizeLabel = "",
  isWin = false,
  spinning = false,
  resultIndex = null,
  onStart,
  onRevealed,
  themeColor = "#C9A55C",
  scratchColor = "#1d2230",
  merchantLogo = null,
  brandName = null,
  disabled = false,
  labelFontFamily = null,
  labelFontWeight = null,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const logoImgRef = useRef(null);
  const [logoReady, setLogoReady] = useState(false);
  const [armed, setArmed] = useState(false);
  const [started, setStarted] = useState(false);
  const [revealed, setRevealed] = useState(false);
  // True once paintCover has fully covered the canvas with the foil.
  // Until then we keep an opaque CSS placeholder over the prize plate
  // so the player can never peek at their prize.
  const [canvasPainted, setCanvasPainted] = useState(false);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);

  // Preload the merchant logo so it can be painted onto the canvas foil.
  useEffect(() => {
    if (!merchantLogo) {
      logoImgRef.current = null;
      setLogoReady(false);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      logoImgRef.current = img;
      setLogoReady(true);
    };
    img.onerror = () => {
      logoImgRef.current = null;
      setLogoReady(false);
    };
    img.src = merchantLogo;
  }, [merchantLogo]);

  useEffect(() => {
    if (resultIndex != null && !armed) setArmed(true);
  }, [resultIndex, armed]);

  /* ---------- Paint the foil ---------- */
  const paintCover = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Graphite base — uses the custom scratchColor (configurable per game)
    const tones = deriveScratchTones(scratchColor);
    const base = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    base.addColorStop(0, tones.light);
    base.addColorStop(0.5, tones.base);
    base.addColorStop(1, tones.dark);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Subtle gold sheen — diagonal band
    const sheen = ctx.createLinearGradient(
      rect.width * 0.1,
      0,
      rect.width * 0.9,
      rect.height
    );
    sheen.addColorStop(0, "rgba(201,165,92,0)");
    sheen.addColorStop(0.45, "rgba(201,165,92,0.35)");
    sheen.addColorStop(0.55, "rgba(245,210,140,0.45)");
    sheen.addColorStop(0.75, "rgba(201,165,92,0.18)");
    sheen.addColorStop(1, "rgba(201,165,92,0)");
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Merchant logo watermark — centered, subtle (behind herringbone + text)
    const logoImg = logoImgRef.current;
    if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
      ctx.save();
      ctx.globalAlpha = 0.18;
      const maxW = rect.width * 0.6;
      const maxH = rect.height * 0.65;
      const ratio = logoImg.naturalWidth / logoImg.naturalHeight;
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }
      ctx.drawImage(
        logoImg,
        (rect.width - w) / 2,
        (rect.height - h) / 2,
        w,
        h
      );
      ctx.restore();
    }

    // Herringbone hatch
    ctx.save();
    ctx.globalAlpha = 0.08;
    ctx.strokeStyle = "#f5d28c";
    ctx.lineWidth = 1;
    for (let i = -rect.height; i < rect.width + rect.height; i += 7) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + rect.height * 0.7, rect.height);
      ctx.stroke();
    }
    ctx.restore();

    // Fine noise
    ctx.save();
    const noise = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < noise.data.length; i += 4) {
      const n = (Math.random() - 0.5) * 16;
      noise.data[i] = Math.min(255, Math.max(0, noise.data[i] + n));
      noise.data[i + 1] = Math.min(255, Math.max(0, noise.data[i + 1] + n));
      noise.data[i + 2] = Math.min(255, Math.max(0, noise.data[i + 2] + n));
    }
    ctx.putImageData(noise, 0, 0);
    ctx.restore();

    // Centered engraved "SCRATCH HERE"
    ctx.save();
    ctx.font =
      "700 13px Outfit, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const letterSpace = 3.8;
    const text = t("play.scratch_here");
    // letter-spaced manually
    const chars = text.split("");
    const totalW = chars.reduce(
      (a, c) => a + ctx.measureText(c).width + letterSpace,
      -letterSpace
    );
    let x = cx - totalW / 2;
    // Glow
    ctx.shadowColor = "rgba(245,210,140,0.55)";
    ctx.shadowBlur = 8;
    ctx.fillStyle = "rgba(245,210,140,0.85)";
    chars.forEach((c) => {
      const w = ctx.measureText(c).width;
      ctx.fillText(c, x + w / 2, cy);
      x += w + letterSpace;
    });
    ctx.restore();

    // Small decorative crosses above / below
    ctx.save();
    ctx.strokeStyle = "rgba(245,210,140,0.55)";
    ctx.lineWidth = 1;
    const drawCross = (ox, oy, s = 4) => {
      ctx.beginPath();
      ctx.moveTo(ox - s, oy);
      ctx.lineTo(ox + s, oy);
      ctx.moveTo(ox, oy - s);
      ctx.lineTo(ox, oy + s);
      ctx.stroke();
    };
    drawCross(cx, cy - 22);
    drawCross(cx, cy + 22);
    ctx.restore();

    ctx.globalCompositeOperation = "destination-out";

    // Foil is fully on the canvas — let the placeholder fade out.
    setCanvasPainted(true);
  }, [t, scratchColor, logoReady]);

  useEffect(() => {
    if (armed && !started && canvasRef.current) {
      requestAnimationFrame(paintCover);
    }
  }, [armed, started, paintCover]);

  /* ---------- Scratch progress ---------- */
  const checkProgress = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext("2d");
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let cleared = 0;
    for (let i = 3; i < data.data.length; i += 80) {
      if (data.data[i] === 0) cleared++;
    }
    const total = data.data.length / 80;
    return cleared / total;
  }, []);

  function pointerPos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return { x: pt.clientX - rect.left, y: pt.clientY - rect.top };
  }

  function scratchAt(x, y) {
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    const last = lastPosRef.current;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 42;
    ctx.beginPath();
    if (last) {
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(x, y);
    } else {
      ctx.arc(x, y, 22, 0, Math.PI * 2);
    }
    ctx.stroke();
    ctx.fill();
    lastPosRef.current = { x, y };
  }

  function handleDown(e) {
    if (!armed || revealed || disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    setStarted(true);
    const { x, y } = pointerPos(e);
    lastPosRef.current = null;
    scratchAt(x, y);
  }
  function handleMove(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = pointerPos(e);
    scratchAt(x, y);
    if (Math.random() < 0.15 && checkProgress() > 0.5) triggerReveal();
  }
  function handleUp() {
    drawingRef.current = false;
    lastPosRef.current = null;
    if (checkProgress() > 0.45) triggerReveal();
  }

  function triggerReveal() {
    if (revealed) return;
    setRevealed(true);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.transition = "opacity 700ms ease";
      canvas.style.opacity = "0";
    }
    setTimeout(() => onRevealed && onRevealed(), 740);
  }

  function handleStartClick() {
    if (spinning || armed || disabled) return;
    setArmed(false);
    setStarted(false);
    setRevealed(false);
    setCanvasPainted(false);
    onStart && onStart();
  }

  // Smart wrap: short labels stay big and on 1 line; long labels split on
  // the space closest to the middle and use a slightly smaller font so 2
  // lines fit comfortably inside the prize frame. Labels without spaces
  // can't be split — they get a smaller single-line font and only truncate
  // if longer than 28 characters.
  const { lines: prizeLines, sizeClass: prizeSizeClass } = wrapPrizeLabel(prizeLabel);

  const accent = isWin ? "#D4AF37" : "#94a3b8";

  return (
    <div
      className="relative mx-auto w-full max-w-[440px] select-none"
      data-testid="scratch-card-root"
    >
      {/* Ambient glow under card */}
      <div
        aria-hidden="true"
        className="absolute -inset-6 rounded-[32px] blur-3xl opacity-40 pointer-events-none"
        style={{
          background: isWin
            ? "radial-gradient(closest-side, #d4af37 0%, transparent 70%)"
            : "radial-gradient(closest-side, #6366f1 0%, transparent 70%)",
        }}
      />

      <div
        className="relative rounded-[26px] overflow-hidden shadow-[0_30px_80px_-25px_rgba(15,23,42,0.6),0_10px_25px_-10px_rgba(15,23,42,0.35)] border border-white/10 dark:border-white/5"
        style={{ aspectRatio: "1.55 / 1" }}
      >
        {/* Prize plate — revealed underneath.
            Dark graphite with warm gold accents (win) or cool stone (lose). */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-8"
          data-testid="scratch-prize"
          style={{
            background: isWin
              ? "radial-gradient(140% 100% at 50% 0%, #2b2418 0%, #141016 55%, #0b0a10 100%)"
              : "radial-gradient(140% 100% at 50% 0%, #1f2330 0%, #121520 55%, #0a0c14 100%)",
          }}
        >
          {/* Ornamental frame */}
          <div
            aria-hidden="true"
            className="absolute inset-3 rounded-[18px] pointer-events-none"
            style={{
              border: `1px solid ${isWin ? "rgba(212,175,55,0.25)" : "rgba(148,163,184,0.20)"}`,
              boxShadow: `inset 0 0 0 1px ${isWin ? "rgba(212,175,55,0.08)" : "rgba(148,163,184,0.06)"}`,
            }}
          />
          {/* Corner sparkles */}
          <Sparkles
            className="absolute top-5 left-5 h-3.5 w-3.5"
            style={{ color: accent, opacity: 0.7 }}
          />
          <Sparkles
            className="absolute top-5 right-5 h-3.5 w-3.5"
            style={{ color: accent, opacity: 0.7 }}
          />
          <Sparkles
            className="absolute bottom-5 left-5 h-3.5 w-3.5"
            style={{ color: accent, opacity: 0.7 }}
          />
          <Sparkles
            className="absolute bottom-5 right-5 h-3.5 w-3.5"
            style={{ color: accent, opacity: 0.7 }}
          />

          <div
            className="text-[10px] uppercase tracking-[0.36em] font-bold"
            style={{ color: accent, letterSpacing: "0.36em" }}
          >
            {isWin ? t("play.scratch_prize") : t("play.scratch_outcome")}
          </div>

          <div
            className={`mt-3 font-semibold tracking-tight leading-[1.05] ${prizeSizeClass}`}
            data-testid="scratch-prize-label"
            style={{
              color: isWin ? "#f5e4bf" : "#e2e8f0",
              textShadow: isWin
                ? "0 2px 24px rgba(212,175,55,0.35)"
                : "0 2px 24px rgba(99,102,241,0.18)",
              fontFamily:
                labelFontFamily ||
                "Outfit, ui-sans-serif, system-ui, sans-serif",
              fontWeight: labelFontWeight || undefined,
            }}
          >
            {!prizeLabel ? (
              "—"
            ) : prizeLines.length === 1 ? (
              prizeLines[0]
            ) : (
              <>
                <span className="block">{prizeLines[0]}</span>
                <span className="block">{prizeLines[1]}</span>
              </>
            )}
          </div>

          <div
            className="mt-4 h-px w-24"
            style={{
              background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            }}
          />

          <div
            className="mt-3 text-[10px] uppercase tracking-[0.26em] font-medium"
            style={{ color: "rgba(226,232,240,0.45)" }}
          >
            {brandName || "QRISE · premium"}
          </div>
        </div>

        {/* Foil placeholder — opaque CSS layer that prevents the player
            from peeking at the prize plate during the brief 16-2000 ms
            window between the canvas mounting (transparent) and
            paintCover finishing. Uses pointer-events: none so the canvas
            still receives all scratch events the moment it's painted. */}
        {armed && !revealed && !canvasPainted && (() => {
          const tones = deriveScratchTones(scratchColor);
          return (
            <div
              aria-hidden="true"
              data-testid="scratch-placeholder"
              className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${tones.light} 0%, ${tones.base} 50%, ${tones.dark} 100%)`,
              }}
            >
              {/* Same hatch as the canvas foil so the swap is invisible */}
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(135deg, #f5d28c 0px, #f5d28c 1px, transparent 1px, transparent 9px)",
                }}
              />
              <div
                className="text-[12px] uppercase tracking-[0.36em] font-bold text-center"
                style={{
                  color: "rgba(245,210,140,0.85)",
                  textShadow: "0 0 8px rgba(245,210,140,0.55)",
                }}
              >
                {t("play.scratch_here")}
              </div>
            </div>
          );
        })()}

        {/* Canvas cover */}
        {armed && !revealed && (
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full touch-none cursor-grab active:cursor-grabbing"
            data-testid="scratch-canvas"
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onMouseUp={handleUp}
            onMouseLeave={handleUp}
            onTouchStart={handleDown}
            onTouchMove={handleMove}
            onTouchEnd={handleUp}
          />
        )}

        {/* Loading overlay */}
        {spinning && !armed && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-testid="scratch-loading"
            style={{
              background:
                "radial-gradient(120% 80% at 50% 0%, #2a3040 0%, #161a26 80%)",
            }}
          >
            <div
              className="flex items-center gap-2.5 text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: "#e2c98a" }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-300" />
              </span>
              {t("play.scratch_loading")}
            </div>
          </div>
        )}

        {/* Idle overlay — call to action */}
        {!armed && !spinning && (() => {
          const idleTones = deriveScratchTones(scratchColor);
          return (
          <button
            type="button"
            onClick={handleStartClick}
            disabled={disabled}
            data-testid="scratch-start-btn"
            className="absolute inset-0 group flex items-center justify-center overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${idleTones.light} 0%, ${idleTones.base} 50%, ${idleTones.dark} 100%)`,
            }}
          >
            {/* Merchant logo watermark — behind shimmer / hatch / text */}
            {merchantLogo && (
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{
                  opacity: 0.18,
                  filter: "grayscale(1) brightness(1.5) contrast(1.05)",
                  mixBlendMode: "screen",
                }}
              >
                <img
                  src={merchantLogo}
                  alt=""
                  className="max-h-[65%] max-w-[65%] object-contain"
                />
              </div>
            )}
            {/* Soft gold shimmer that slides on hover */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-60 transition-transform duration-[1600ms] ease-out group-hover:translate-x-[8%]"
              style={{
                background:
                  "linear-gradient(115deg, transparent 30%, rgba(245,210,140,0.22) 48%, rgba(245,210,140,0.05) 56%, transparent 70%)",
              }}
            />
            {/* Fine diagonal hatch */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, #f5d28c 0px, #f5d28c 1px, transparent 1px, transparent 9px)",
              }}
            />
            {/* Ornamental frame */}
            <div
              aria-hidden="true"
              className="absolute inset-4 rounded-[16px]"
              style={{
                border: "1px solid rgba(212,175,55,0.28)",
                boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.08)",
              }}
            />
            <div className="relative text-center px-6">
              <div
                className="text-[10px] uppercase tracking-[0.36em] font-bold"
                style={{ color: "#d4af37" }}
              >
                {t("play.scratch_tap_to_reveal")}
              </div>
              <div
                className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight"
                style={{
                  color: "#f5e4bf",
                  fontFamily: "Outfit, ui-sans-serif, system-ui, sans-serif",
                  textShadow: "0 2px 24px rgba(212,175,55,0.35)",
                }}
              >
                {t("play.scratch_headline")}
              </div>
              <div className="mt-5 inline-flex items-center justify-center">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold tracking-[0.2em] uppercase transition-transform group-hover:scale-[1.03] group-active:scale-[0.98]"
                  style={{
                    background:
                      "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                    color: "#1a1410",
                    boxShadow:
                      "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.25)",
                  }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("play.scratch_start")}
                </span>
              </div>
            </div>
          </button>
          );
        })()}
      </div>

      {/* Hint below card */}
      {armed && !revealed && (
        <p className="text-center mt-4" data-testid="scratch-hint">
          <span className="inline-block rounded-full bg-black/45 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.26em] font-semibold text-white/90">
            ✦ {t("play.scratch_hint")} ✦
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * Wrap a prize label for the Scratch Card so long names don't get hard-cut.
 *
 * Rules (chosen so the prize stays visually prominent — never tiny):
 *  - ≤ 18 chars                       → 1 line, big font  (text-4xl sm:text-5xl)
 *  - 19+ chars, no space              → 1 line, medium font (text-2xl sm:text-3xl);
 *                                       hard-truncate at 28 chars with `…`
 *  - 19+ chars, with space            → 2 lines split on the space closest to
 *                                       the middle. Each line capped at 24
 *                                       chars (truncated with `…` if longer).
 *                                       Font: text-3xl sm:text-4xl when both
 *                                       lines ≤ 16 chars, else text-2xl sm:text-3xl
 */
function wrapPrizeLabel(label) {
  const text = (label || "").trim();
  if (!text) return { lines: [""], sizeClass: "text-4xl sm:text-5xl" };

  if (text.length <= 18) {
    return { lines: [text], sizeClass: "text-4xl sm:text-5xl" };
  }

  if (!text.includes(" ")) {
    const capped = text.length > 28 ? text.slice(0, 27) + "…" : text;
    return { lines: [capped], sizeClass: "text-2xl sm:text-3xl" };
  }

  const words = text.split(/\s+/);
  let best = {
    line1: words[0],
    line2: words.slice(1).join(" "),
    diff: Infinity,
  };
  for (let i = 1; i < words.length; i++) {
    const l1 = words.slice(0, i).join(" ");
    const l2 = words.slice(i).join(" ");
    const d = Math.abs(l1.length - l2.length);
    if (d < best.diff) best = { line1: l1, line2: l2, diff: d };
  }
  const lineMax = 24;
  const line1 =
    best.line1.length > lineMax ? best.line1.slice(0, lineMax - 1) + "…" : best.line1;
  const line2 =
    best.line2.length > lineMax ? best.line2.slice(0, lineMax - 1) + "…" : best.line2;
  const maxLine = Math.max(line1.length, line2.length);
  const sizeClass =
    maxLine <= 16 ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl";
  return { lines: [line1, line2], sizeClass };
}


function deriveScratchTones(hex) {
  // Returns 3 shades (light / base / dark) for the 135° foil gradient.
  let clean = (hex || "").trim();
  if (!/^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(clean)) {
    return { light: "#2a3040", base: "#1d2230", dark: "#161a26" };
  }
  if (clean.startsWith("#")) clean = clean.slice(1);
  if (clean.length === 3) clean = clean.split("").map((c) => c + c).join("");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const shift = (delta) => {
    const toHex = (n) =>
      Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
    return `#${toHex(r + delta)}${toHex(g + delta)}${toHex(b + delta)}`;
  };
  return { light: shift(18), base: `#${clean}`, dark: shift(-22) };
}

